/**
 * What the neighbours are owed, and what somebody still has to post.
 *
 * Roof Board builds the packets — the filled-in letter, the address labels and
 * a cover sheet — and emails them to whoever prints and mails (ADR 0149). This
 * is the other half of that: the list the person holding the envelopes works
 * from, and the record of what actually went out.
 *
 * Josh, on why the tick matters: "That gives us a record of what actually went
 * out, plus reminders for anything still waiting." An email that sent is not a
 * letter that went, and before this screen the two were the same fact.
 *
 * Three states are worth acting on and each has its own thing to press:
 *
 *   - **Ready to print.** Open the three files, print, mail, tick.
 *   - **Needs attention.** Something stopped it, in a sentence. Usually a
 *     blank in Tools or a job whose address was never pinned on the map; both
 *     are fixed elsewhere and then put back in the queue from here.
 *   - **Preparing.** Nothing to do. It is in the queue and the next sweep will
 *     take it — shown so that "nothing has happened" is visibly different from
 *     "nothing is owed".
 */

import { useCallback, useMemo, useState } from "react";

import { NEIGHBOUR_STATUS_LABELS, LETTER_NAMES } from "@roof-hawk/core";

import { ErrorNote, Figure, Modal, Scoreboard } from "../../components/ui";
import { fmtDate } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import {
  cancelNeighbourMailing,
  fetchNeighbourMailings,
  fetchNeighbourRecipients,
  retryNeighbourMailing,
  setNeighbourMailingMailed,
  signPacketFile,
  type NeighbourMailing,
} from "../../lib/queries";
import { useSession } from "../../session";

/** What each flag means, said to the person who has to do something about it. */
const FLAGS: Record<string, string> = {
  canceled_before_mailing:
    "The job was lost or closed before these went out — they have been stopped.",
  unmailed_at_completion:
    "The roof was finished before this one was posted. A letter saying work " +
    "is coming is probably not worth sending now.",
  notification_uncertain:
    "The packet is ready, but Roof Board is not sure the email arrived. " +
    "Check before anybody resends it.",
};

const FILTERS = [
  ["open", "To do"],
  ["ready", "Ready to print"],
  ["blocked", "Needs attention"],
  ["mailed", "Mailed"],
  ["all", "Everything"],
] as const;

type Filter = (typeof FILTERS)[number][0];

const where = (row: NeighbourMailing): string =>
  [row.jobs?.address, row.jobs?.city].filter(Boolean).join(", ") ||
  "an address that has gone";

export function NeighbourMailingList() {
  const { isAdmin } = useSession();
  const mailings = useAsync(fetchNeighbourMailings, []);
  const [filter, setFilter] = useState<Filter>("open");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showing, setShowing] = useState<NeighbourMailing | null>(null);

  // Its own memo: an empty list built inline is a new array on every render,
  // and the two memos below would then recompute for ever.
  const rows = useMemo(() => mailings.data ?? [], [mailings.data]);

  const counts = useMemo(
    () => ({
      ready: rows.filter((row) => row.status === "ready").length,
      blocked: rows.filter((row) => row.status === "blocked").length,
      preparing: rows.filter((row) => row.status === "preparing").length,
      mailed: rows.filter((row) => row.status === "mailed").length,
      households: rows
        .filter((row) => row.status === "mailed")
        .reduce((sum, row) => sum + (row.recipient_count ?? 0), 0),
    }),
    [rows],
  );

  const visible = useMemo(
    () =>
      rows.filter((row) => {
        if (filter === "all") return true;
        if (filter === "open")
          return ["ready", "blocked", "preparing"].includes(row.status);
        return row.status === filter;
      }),
    [rows, filter],
  );

  const act = useCallback(
    async (id: string, work: () => Promise<void>) => {
      setBusy(id);
      setError(null);
      try {
        await work();
        await mailings.reload();
      } catch (problem) {
        setError(problem instanceof Error ? problem.message : String(problem));
      } finally {
        setBusy(null);
      }
    },
    [mailings],
  );

  /** Open one of a packet's PDFs in a new tab. */
  const open = useCallback(async (path: string) => {
    setError(null);
    try {
      const url = await signPacketFile(path);
      if (url) window.open(url, "_blank", "noopener");
      else setError("That file could not be opened.");
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    }
  }, []);

  return (
    <div className="list">
      <div className="listhead">
        <h2>Neighbour letters</h2>
        <div className="lh-right">
          <span className="count">{visible.length}</span>
          <select
            className="datefilter"
            aria-label="Which mailings"
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
          >
            {FILTERS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ErrorNote error={error ?? mailings.error} />

      <Scoreboard>
        <Figure
          label="Ready to print"
          value={String(counts.ready)}
          flag={counts.ready > 0 ? "open" : "clear"}
        />
        <Figure label="Preparing" value={String(counts.preparing)} />
        <Figure
          label="Needs attention"
          value={String(counts.blocked)}
          tone={counts.blocked > 0 ? "bad" : "good"}
          flag={counts.blocked > 0 ? "open" : "clear"}
        />
        <Figure
          label="Mailed"
          value={String(counts.mailed)}
          note={`${counts.households} households`}
        />
      </Scoreboard>

      <div className="block">
        {mailings.loading ? (
          <div className="tnone">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="tnone">
            {filter === "open"
              ? "Nothing waiting. Every neighbour letter owed has been posted."
              : "Nothing here."}
          </div>
        ) : (
          <div className="ewrap">
            <table className="etable" aria-label="Neighbour letters">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Where</th>
                  <th>Letter</th>
                  <th className="num">Letters</th>
                  <th>State</th>
                  <th>Packet</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <a
                        href={`#job=${row.job_id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`job=${row.job_id}`);
                        }}
                      >
                        {row.jobs?.job_number ?? "—"}
                      </a>
                    </td>
                    <td>{where(row)}</td>
                    <td>{LETTER_NAMES[row.kind]}</td>
                    <td className="num">
                      {row.recipient_count > 0 ? (
                        <button
                          type="button"
                          className="linkish"
                          onClick={() => setShowing(row)}
                        >
                          {row.recipient_count}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {NEIGHBOUR_STATUS_LABELS[
                        row.status as keyof typeof NEIGHBOUR_STATUS_LABELS
                      ] ?? row.status}
                      {row.status === "mailed" && row.mailed_at && (
                        <span className="na-hint">
                          {" "}
                          · {fmtDate(row.mailed_at)}
                        </span>
                      )}
                      {row.status === "preparing" && row.attempts > 0 && (
                        <span className="na-hint">
                          {" "}
                          · try {row.attempts + 1}
                        </span>
                      )}
                      {row.flag && FLAGS[row.flag] && (
                        <div className="na-hint">{FLAGS[row.flag]}</div>
                      )}
                      {row.error && row.status !== "mailed" && (
                        <div className="na-hint">{row.error}</div>
                      )}
                      {row.shortfall_note && (
                        <div className="na-hint">{row.shortfall_note}</div>
                      )}
                    </td>
                    <td>
                      {row.letter_path ? (
                        <>
                          <button
                            type="button"
                            className="linkish"
                            onClick={() => void open(row.letter_path!)}
                          >
                            Letter
                          </button>
                          {row.labels_path && (
                            <>
                              {" · "}
                              <button
                                type="button"
                                className="linkish"
                                onClick={() => void open(row.labels_path!)}
                              >
                                Labels
                              </button>
                            </>
                          )}
                          {row.cover_path && (
                            <>
                              {" · "}
                              <button
                                type="button"
                                className="linkish"
                                onClick={() => void open(row.cover_path!)}
                              >
                                Cover
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="na-hint">not built yet</span>
                      )}
                    </td>
                    <td className="num">
                      {(row.status === "ready" || row.status === "mailed") && (
                        <button
                          type="button"
                          className={
                            row.status === "mailed" ? "ghost" : "primary"
                          }
                          disabled={busy === row.id}
                          onClick={() =>
                            void act(row.id, () =>
                              setNeighbourMailingMailed(
                                row.id,
                                row.status !== "mailed",
                              ),
                            )
                          }
                        >
                          {row.status === "mailed" ? "Not mailed" : "Mailed"}
                        </button>
                      )}
                      {row.status === "blocked" && (
                        <button
                          type="button"
                          className="primary"
                          disabled={busy === row.id}
                          onClick={() =>
                            void act(row.id, () =>
                              retryNeighbourMailing(row.id),
                            )
                          }
                        >
                          Try again
                        </button>
                      )}
                      {isAdmin &&
                        ["ready", "blocked", "preparing"].includes(
                          row.status,
                        ) && (
                          <button
                            type="button"
                            className="ghost"
                            disabled={busy === row.id}
                            onClick={() =>
                              void act(row.id, () =>
                                cancelNeighbourMailing(row.id),
                              )
                            }
                          >
                            Stop
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showing && (
        <RecipientList mailing={showing} onClose={() => setShowing(null)} />
      )}
    </div>
  );
}

/** The households one mailing went to, for anybody who wants to check. */
function RecipientList({
  mailing,
  onClose,
}: {
  mailing: NeighbourMailing;
  onClose: () => void;
}) {
  const recipients = useAsync(
    () => fetchNeighbourRecipients(mailing.id),
    [mailing.id],
  );

  return (
    <Modal title={`Who hears about ${where(mailing)}`} onClose={onClose}>
      <ErrorNote error={recipients.error} />
      {recipients.loading ? (
        <div className="tnone">Loading…</div>
      ) : (
        <div className="ewrap">
          <table className="etable" aria-label="Households">
            <thead>
              <tr>
                <th>Address</th>
                <th>Town</th>
                <th className="num">Miles</th>
              </tr>
            </thead>
            <tbody>
              {(recipients.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.line1}
                    {row.incomplete && (
                      <span className="na-hint"> · address incomplete</span>
                    )}
                  </td>
                  <td>
                    {[row.city, row.state, row.postal_code]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </td>
                  <td className="num">
                    {row.distance_miles === null
                      ? "—"
                      : Number(row.distance_miles).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
